<template>
  <div
    class="group w-full"
    :class="{
      'max-w-[333px]': !fullWidth,
    }"
  >
    <t-label v-if="label" :opened="opened">{{ label }}</t-label>
    <div
      ref="outsideDrop"
      class="t-dropdown relative box-border rounded-md border border-solid border-gray-600 bg-gray-600 font-inter text-xs font-medium text-gray-50 transition duration-300 group-focus-within:text-gray-10 group-focus-within:outline group-focus-within:outline-2 group-focus-within:outline-accent-1 group-hover:border-gray-400"
      :class="{
        '!rounded-b-none !border-transparent !text-gray-10 outline outline-2 outline-accent-1':
          opened,
      }"
    >
      <button
        v-tooltip.bottom="{
          content:
            typeof selected === 'string' && selected.length > 15
              ? selected
              : '',
          delay: { show: 250 },
        }"
        class="box-border flex w-full items-center justify-between overflow-hidden rounded-md bg-gray-600 pl-2.5 pr-1 font-inter text-xs font-medium leading-[30px] text-gray-50 transition duration-300 focus:text-gray-10"
        :class="{
          '!text-gray-10': opened,
        }"
        aria-haspopup="true"
        :aria-expanded="opened"
        @click="opened = !opened"
      >
        <p
          v-if="
            !$slots.selectedContent && placeholder && !selected?.[dataLabel]
          "
          class="text truncate"
        >
          {{ placeholder }}
        </p>
        <p
          v-if="!$slots.selectedContent && selected?.[dataLabel]"
          class="text truncate"
        >
          {{ selected[dataLabel] }}
        </p>
        <p
          v-if="!$slots.selectedContent && !selected && !placeholder && data[0]"
          class="text truncate"
        >
          {{ data[0][dataLabel] }}
        </p>

        <slot name="selected-content" />

        <div
          class="flex h-[20px] w-[20px] cursor-pointer items-center justify-center"
        >
          <font-awesome-icon
            :icon="['fas', 'caret-down']"
            class="text-gray-100"
          />
        </div>
      </button>

      <ul v-if="!$slots.content && opened">
        <li
          v-if="showSearch"
          class="border-0 border-b border-solid border-b-gray-400 !bg-gray-600"
        >
          <SearchIcon />
          <input
            ref="queryInput"
            v-model="query"
            :placeholder="searchPlaceholder"
            type="text"
            class="rounded-0 box-border block w-full border-none bg-gray-600 p-1 font-inter text-xs leading-[16px] text-gray-50 !outline-none transition duration-300 placeholder:text-gray-100 focus:!text-gray-10"
          />
        </li>
        <VueCustomScrollbar
          :style="{ height: calculatedHeight + 'px' }"
          :options="{
            wheelPropagation: false,
          }"
          @ps-y-reach-end="loadMore"
        >
          <li
            v-for="option in data"
            :key="getOptionKey(option)"
            @click="selectionChanged(option)"
          >
            <p
              v-tooltip="{
                content:
                  getOptionLabel(option).length > 18
                    ? getOptionLabel(option)
                    : '',
                delay: { show: 250 },
              }"
              class="truncate pl-0"
            >
              {{ getOptionLabel(option) }}
            </p>
          </li>
        </VueCustomScrollbar>
        <li
          v-if="!loading && data.length === 0"
          class="!cursor-default !justify-center px-3 py-2 text-gray-200 hover:!bg-transparent"
        >
          <p class="m-0">{{ noDataText }}</p>
        </li>
        <li v-if="loading" class="flex justify-center">
          <t-loading-spinner></t-loading-spinner>
        </li>
      </ul>

      <div v-show="opened" v-else>
        <slot name="content" />
      </div>
    </div>
  </div>
</template>
<script lang="ts" setup>
import TLabel from "../TLabel/TLabel.vue";
import TLoadingSpinner from "../TLoadingSpinner/TLoadingSpinner.vue";
import { SearchIcon } from "@topol/icons";
import { TDropdownOption } from "../../types/TDropdownOptions/TDropdownOptions";
import "vue3-perfect-scrollbar/style.css";
import { PerfectScrollbar as VueCustomScrollbar } from "vue3-perfect-scrollbar";
import { vTooltip } from "floating-vue";
import "floating-vue/dist/style.css";
import { onClickOutside } from "@vueuse/core";
import { ref, computed, useTemplateRef, watch } from "vue";

interface Props {
  data?: Array<any>;
  dataLabel?: string; //key which acts as a label of the selected data
  dataKey?: string; //key which acts as a key of the selected data
  selected?: any;
  placeholder?: string;
  roundedLeft?: boolean;
  roundedRight?: boolean;
  roundedFull?: boolean;
  label?: string;
  noBorderLeft?: boolean;
  loading?: boolean;
  shouldLoadMore?: boolean;
  showSearch?: boolean;
  searchPlaceholder?: string;
  noDataText?: string;
  fullWidth?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  data: () => [],
  dataLabel: "id",
  dataKey: "id",
  selected: "",
  placeholder: "",
  roundedLeft: false,
  roundedRight: false,
  roundedFull: false,
  label: "",
  noBorderLeft: false,
  loading: false,
  shouldLoadMore: false,
  showSearch: false,
  searchPlaceholder: "Search...",
  noDataText: "No data found",
});

const emit = defineEmits<{
  (e: "selection-changed", option: any): void;
  (e: "load-more"): void;
  (e: "search", query: string): void;
}>();

const opened = ref(false);
const outsideDrop = useTemplateRef("outsideDrop");
const query = ref("");

const calculatedHeight = computed(() => {
  if (props.data.length < 6) {
    return props.data.length * 32 + 2;
  }

  return props.showSearch ? 209 : 242;
});

function selectionChanged(option: TDropdownOption) {
  emit("selection-changed", option);
  opened.value = false;
}

function getOptionLabel(option: TDropdownOption): string {
  return option[props.dataLabel] || "";
}

function getOptionKey(option: TDropdownOption): string {
  return option[props.dataKey];
}

watch(query, (newQuery) => {
  emit("search", newQuery);
});

watch(opened, (newVal) => {
  if (!newVal) {
    query.value = "";
  }
});

onClickOutside(outsideDrop, () => {
  opened.value = false;
});

function loadMore() {
  if (!props.loading && props.shouldLoadMore) {
    emit("load-more");
  }
}

defineExpose({
  opened,
});
</script>
<style lang="scss">
.t-dropdown {
  ul {
    position: absolute;
    z-index: 10;
    width: calc(100% + 2px);

    margin-block-start: 31px;
    margin-block-end: 0em;
    margin-inline-start: 0px;
    margin-inline-end: 0px;
    padding-inline-start: 0px;
    background-color: var(--c-g600);

    max-height: 240px;
    margin-left: -3px;
    overflow: hidden;
    padding: 0;
    top: 0;
    border: 2px solid var(--c-accent-1);
    border-top: none;
    border-bottom-left-radius: 6px;
    border-bottom-right-radius: 6px;
    li {
      font-size: 0.75rem;
      font-family: "Inter", sans-serif;
      font-weight: 500;
      line-height: 1;
      // @include font(
      //   --chip-font,
      //   var(--font-medium),
      //   12px,
      //   1,
      //   var(--font-family-primary)
      // );
      cursor: pointer;
      display: flex;
      align-items: center;
      padding: 4px 12px;
      line-height: 24px;
      color: var(--c-g100);
      transition: 0.3s;
      p {
        transition: 0.3s;
        color: var(--c-g100);
      }
      &:hover {
        transition:
          color 150ms ease-in-out,
          background-color 150ms ease-in-out,
          border 150ms ease-in-out;
        background-color: var(--c-g400);
        p {
          color: var(--c-g50);
        }
      }
    }
  }

  .ps__rail-y {
    right: 0 !important;
    left: auto !important;
  }
}
</style>
